import { ChangeEvent, useEffect, useState } from "react";
import { Autocomplete } from "@mui/material";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
	changeCorrectionInModelConfig,
	changeModelnameInModelConfig,
	changeSettingVoiceInModelConfig,
	changeThresholdInModelConfig,
	deleteModelConfig,
	selectActiveRobotVersion,
} from "../../../store/ses/sesRobotSlice";
import useAccessRight from "../../../hooks/useAccessRight";
import useTranslate from "../../../hooks/useTranslate";
import { SES } from "../../../constants/accessRights";
import { CORRECTION_OPTIONS_LIST, ROBOT_MODEL_SERVICE_TYPE_LIST } from "../../../constants/robotConfigLists";
import { colorRed } from "../../../constants/colors";
import { CorrectionType } from "../../../types/smcTypes";
import { ServiceTypeModelRobot } from "../../../types/cloudTypes";
import { IModelsProps } from "./Models.props";
import styles from "./Models.module.scss";
import { CustomMenuItem, CustomTextField } from "@/ui";
import { ButtonIcon, RenderOption } from "@/ui";

const Models = ({ serviceType, modelName, configData, modelsList, changeFlgHandler }: IModelsProps): JSX.Element => {
	const [selectType, setSelectType] = useState<ServiceTypeModelRobot>(serviceType); // тип
	const [inputModelName, setInputModelName] = useState<string>(modelName); // название модели
	const [threshold, setThreshold] = useState<number>(0); // порог доверия (для smc/see)
	const [selectCorrection, setSelectCorrection] = useState<CorrectionType>(1); // коррекция (для smc)

	const [rate, setRate] = useState<number>(0); // скорость (для tts)
	const [pitch, setPitch] = useState<number>(0); // тональность (для tts)
	const [volume, setVolume] = useState<number>(0); // громкость (для tts)
	const [frequency, setFrequency] = useState<number>(22050); // частота (для tts)

	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за настройками модели данными и вписываем значения в поля
	useEffect(() => {
		if ("threshold" in configData) setThreshold(configData.threshold);
		else if ("confidenceThreshold" in configData) {
			setThreshold(configData.confidenceThreshold);
			setSelectCorrection(configData.correction);
		} else if ("rate" in configData) {
			setRate(configData.rate);
			setPitch(configData.pitch);
			setVolume(configData.volume);
			setFrequency(configData.frequency);
		}
	}, [configData]);

	// следим за полем порога доверия
	useEffect(() => {
		// через пол-секунды бездействия после окончания ввода
		const handler = setTimeout(() => {
			if ("threshold" in configData && configData.threshold !== threshold) {
				dispatch(changeThresholdInModelConfig({ serviceType, modelName, threshold })); // изменяем порог доверия
				changeFlgHandler("models");
			} else if ("confidenceThreshold" in configData && configData.confidenceThreshold !== threshold) {
				dispatch(changeThresholdInModelConfig({ serviceType, modelName, threshold })); // изменяем порог доверия
				changeFlgHandler("models");
			}
		}, 500);

		return () => {
			clearTimeout(handler); // сбрасываем timeout, если продолжается ввод
		};
	}, [threshold]);

	// следим за полями скорости, тональности, громкости
	useEffect(() => {
		// через пол-секунды бездействия после окончания ввода
		const handler = setTimeout(() => {
			if ("rate" in configData && (configData.rate !== rate || configData.pitch !== pitch || configData.volume !== volume)) {
				dispatch(changeSettingVoiceInModelConfig({ serviceType, modelName, setting: { rate, pitch, volume, frequency } })); // изменяем настройки голоса
				changeFlgHandler("models");
			}
		}, 500);

		return () => {
			clearTimeout(handler); // сбрасываем timeout, если продолжается ввод
		};
	}, [rate, pitch, volume]);

	// обработчик изменения модели
	const changeModelHandler = (): void => {
		if (modelName !== inputModelName) {
			dispatch(changeModelnameInModelConfig({ serviceType, oldModelName: modelName, newModelName: inputModelName }));
			changeFlgHandler("models");
		}
	};

	// обработчик изменения коррекции (для smc)
	const changeCorrectionHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		const value = +e.target.value as CorrectionType;
		setSelectCorrection(value);
		dispatch(
			changeCorrectionInModelConfig({
				modelName,
				correction: value,
			})
		);
		changeFlgHandler("models");
	};

	// обработчик изменения частоты
	const changeFrequencyHandler = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		setFrequency(+e.target.value);
		dispatch(changeSettingVoiceInModelConfig({ serviceType, modelName, setting: { rate, pitch, volume, frequency: +e.target.value } })); // изменяем настройки голоса
		changeFlgHandler("models");
	};

	// обработчик удаления настроек модели
	const deleteModelConfigHandler = (): void => {
		dispatch(deleteModelConfig({ serviceType, modelName }));
		changeFlgHandler("models");
	};

	return (
		<div className={styles.container}>
			<div className={styles.block}>
				{/* тип */}
				<CustomTextField
					select
					fullWidth
					label={translate("select_type")}
					value={selectType}
					onChange={(e) => setSelectType(e.target.value as ServiceTypeModelRobot)}
					disabled
					hint={translate("hint_modelType")}
				>
					{ROBOT_MODEL_SERVICE_TYPE_LIST.map(({ type, translation }) => (
						<CustomMenuItem key={type} value={type}>
							{translate(translation)}
						</CustomMenuItem>
					))}
				</CustomTextField>

				{/* модель */}
				<Autocomplete
					fullWidth
					freeSolo
					options={modelsList}
					value={inputModelName}
					onChange={(_, value) => {
						setInputModelName(value ? value : "");
						changeFlgHandler("models"); // ставим флаг о несохраненных данных
					}}
					onBlur={changeModelHandler}
					disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
					renderInput={(params) => (
						<CustomTextField
							{...params}
							hint={translate("hint_modelModel")}
							required
							label={translate(serviceType === "tts" ? "input_voice" : "input_model")}
							onChange={(e) => {
								setInputModelName(e.target.value);
								changeFlgHandler("models"); // ставим флаг о несохраненных данных
							}}
							error={!inputModelName}
							// InputProps={{
							// 	...params.InputProps, // важно прокинуть параметры
							// 	endAdornment: (
							// 		<div style={{ marginTop: '-7px' }}>
							// 			{clusterServer.status === RequestStatus.LOADING &&
							// 				<ProgressCircle isBtnDisabled />
							// 			}
							// 			{params.InputProps.endAdornment} {/* важно дописать параметры */}
							// 		</div>
							// 	),
							// }}
						/>
					)}
					renderOption={(props, option) => {
						return <RenderOption {...props}>{option}</RenderOption>;
					}}
				/>

				{/* порог доверия */}
				{(serviceType === "smc" || serviceType === "see") && (
					<CustomTextField
						fullWidth
						required
						label={translate("input_confidenceThreshold")}
						type="Number"
						value={threshold}
						onChange={(e) => setThreshold(Number(e.target.value))}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						error={threshold < 0 || threshold > 100}
						InputProps={{
							inputProps: { step: 1, min: 0, max: 100 },
						}}
						hint={translate("hint_modelThresholdOfTrust")}
					/>
				)}

				{/* коррекция */}
				{serviceType === "smc" && (
					<CustomTextField
						select
						fullWidth
						required
						label={translate("select_correction")}
						value={selectCorrection}
						onChange={changeCorrectionHandler}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						hint={translate("hint_modelCorrection")}
					>
						{CORRECTION_OPTIONS_LIST.map(({ option, translation }) => (
							<CustomMenuItem key={option} value={option}>
								{translate(translation)}
							</CustomMenuItem>
						))}
					</CustomTextField>
				)}

				{/* удаление настройки модели */}
				{isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft" && (
					<ButtonIcon icon={faTrashCan} iconColor={colorRed} iconSize="1x" title={translate("button_delete")} onClick={deleteModelConfigHandler} />
				)}
			</div>

			{serviceType === "tts" && (
				<div className={styles.block}>
					{/* скорость */}
					<CustomTextField
						fullWidth
						required
						label={translate("input_rate")}
						type="Number"
						value={rate}
						onChange={(e) => setRate(Number(e.target.value))}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						error={rate < 10 || rate > 200}
						InputProps={{
							inputProps: { step: 1, min: 10, max: 200 },
						}}
					/>
					{/* тональность */}
					<CustomTextField
						fullWidth
						required
						label={translate("input_pitch")}
						type="Number"
						value={pitch}
						onChange={(e) => setPitch(Number(e.target.value))}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						error={pitch < 10 || pitch > 200}
						InputProps={{
							inputProps: { step: 1, min: 10, max: 200 },
						}}
					/>
					{/* громкость */}
					<CustomTextField
						fullWidth
						required
						label={translate("input_volume")}
						type="Number"
						value={volume}
						onChange={(e) => setVolume(Number(e.target.value))}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						error={volume < 10 || volume > 200}
						InputProps={{
							inputProps: { step: 1, min: 10, max: 200 },
						}}
					/>
					{/* частота */}
					<CustomTextField
						select
						fullWidth
						required
						label={translate("select_frequency")}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						value={frequency}
						onChange={changeFrequencyHandler}
					>
						{[8000, 16000, 22050].map((frequency) => (
							<CustomMenuItem key={frequency} value={frequency}>
								{Math.round(frequency / 1000)} {translate("title_kHz")}
							</CustomMenuItem>
						))}
					</CustomTextField>
				</div>
			)}
		</div>
	);
};

export default Models;
